import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A81026",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
};

// ─── PER-SCALE ACCENT COLOURS ─────────────────────────────────────────────────
const SCALE_COLOR = {
  I:  "#A80D2C",   // Internal     — brand red
  EO: "#1A7A4A",   // Ext-Others   — forest green
  EC: "#D79A1E",   // Ext-Chance   — gold
};

// ─── SCALE DEFINITIONS ────────────────────────────────────────────────────────
const SCALE_DEFS = [
  {
    code:    "I",
    key:     "internal",
    label:   "Internal",
    icon:    "🎯",
    tagline: "Personal agency, effort, competence, and ownership.",
    overview:
      "People who score high on Internal Locus of Control believe their actions, effort, and decisions are the primary drivers of what happens to them. They take ownership of outcomes, persist in the face of obstacles, and rarely attribute failures to external forces.",
    orgNote:
      "A high Internal orientation is strongly associated with initiative, accountability, and resilience in professional settings. In leadership, it supports execution discipline and a bias toward action. The risk is underestimating the legitimate role of context, relationships, and systemic factors in shaping outcomes.",
    devSteps: [
      "Continue owning outcomes — your accountability mindset is a core professional asset.",
      "Practise mapping stakeholder influences before committing to a plan.",
      "Build feedback loops so effort is tied to visible, measurable results.",
      "Guard against frustration when results do not match effort in genuinely uncertain situations.",
    ],
  },
  {
    code:    "EO",
    key:     "external_others",
    label:   "External-Others",
    icon:    "🤝",
    tagline: "Influence of seniors, power holders, and key others.",
    overview:
      "People who score high on External-Others believe that outcomes are significantly shaped by the decisions, preferences, and actions of powerful others — bosses, senior leaders, influential peers, or gatekeepers. They are attuned to hierarchy and relational dynamics.",
    orgNote:
      "A moderate EO orientation supports political awareness and stakeholder sensitivity — valuable in complex organisations. A high EO score can indicate over-reliance on approval or authority, reducing initiative and self-direction. Development should help the person distinguish between genuine dependency and strategic alignment.",
    devSteps: [
      "Identify decisions you are delaying because you are waiting for approval that may not be necessary.",
      "Build a track record of self-initiated action in lower-stakes situations.",
      "Use stakeholder awareness as a strategic asset, not a reason to defer all choices.",
      "Explore whether senior influence is a real constraint or a limiting belief.",
    ],
  },
  {
    code:    "EC",
    key:     "external_chance",
    label:   "External-Chance",
    icon:    "🎲",
    tagline: "Luck, timing, chance, and uncertainty.",
    overview:
      "People who score high on External-Chance believe that luck, fate, timing, and unpredictable circumstances play a significant role in determining outcomes. They may be less confident that effort consistently produces results.",
    orgNote:
      "A moderate EC orientation reflects realistic awareness that the world is uncertain — a healthy attribute in volatile environments. High EC scores may indicate a fatalistic attribution style that reduces planning, effort, and risk-taking. The key development challenge is separating genuine unpredictability from learned helplessness.",
    devSteps: [
      "Reframe 'luck' events: identify which factors in a past outcome were actually controllable.",
      "Build a habit of proactive planning even in uncertain conditions.",
      "Use scenario-based thinking to prepare for multiple outcomes, reducing dependence on favourable chance.",
      "Pair awareness of uncertainty with a clear personal action plan that is independent of outcomes.",
    ],
  },
];
// ─── PROFILE MAP (from LOCO_V2 Profile Mapping) ───────────────────────────────
const PROFILE_MAP = {
  P01: { label: "Strong Internal",                               dominance_pattern: "Internal dominant" },
  P02: { label: "Internal-leaning Mixed",                        dominance_pattern: "Internal dominant with external awareness" },
  P03: { label: "Balanced Profile",                              dominance_pattern: "Balanced / mixed" },
  P04: { label: "Others-leaning Mixed",                          dominance_pattern: "Other-dominant with mixed awareness" },
  P05: { label: "Strong External-Others",                        dominance_pattern: "EO dominant" },
  P06: { label: "Strong External-Chance",                        dominance_pattern: "EC dominant" },
  P07: { label: "Internal with External Pressure",               dominance_pattern: "Internal with EO pressure" },
  P08: { label: "External-Others with Chance Overlay",           dominance_pattern: "EO dominant with chance overlay" },
  P09: { label: "Chance-dominant External",                      dominance_pattern: "EC dominant with EO overlay" },
  P11: { label: "High Internal with Moderate EO",                dominance_pattern: "Internal with political awareness" },
  P12: { label: "High Internal with Moderate EC",                dominance_pattern: "Internal with uncertainty awareness" },
  P13: { label: "High EO with Moderate I",                       dominance_pattern: "Political sensitivity with some agency" },
  P14: { label: "High EO with Moderate EC",                      dominance_pattern: "Political sensitivity with uncertainty overlay" },
  P15: { label: "High EC with Moderate I",                       dominance_pattern: "Uncertainty dominant with some agency" },
  P16: { label: "High EC with Moderate EO",                      dominance_pattern: "Uncertainty plus hierarchy" },
  P17: { label: "Low on all three",                              dominance_pattern: "Low-intensity pattern" },
  P18: { label: "High on all three",                             dominance_pattern: "Complex high-endorsement pattern" },
  P19: { label: "Two-way tie: I and EO",                         dominance_pattern: "Internal and others balanced" },
  P20: { label: "Two-way tie: I and EC",                         dominance_pattern: "Internal and chance balanced" },
  P21: { label: "Two-way tie: EO and EC",                        dominance_pattern: "Others and chance dominant" },
  P22: { label: "Wide spread profile",                           dominance_pattern: "Highly differentiated profile" },
  P23: { label: "Developmental internal profile",                dominance_pattern: "Growth-oriented internal" },
  P24: { label: "Defensive external profile",                    dominance_pattern: "External defense pattern" },
  P25: { label: "Autonomous but environment-sensitive profile",  dominance_pattern: "Self-directed with realism" },
  P26: { label: "Pragmatic mixed profile",                       dominance_pattern: "Pragmatic blend" },
};

// ─── BAND LOGIC ───────────────────────────────────────────────────────────────
// Scores range 10–40: Low = 10–20, Moderate = 21–29, High = 30–40
const BAND_RANGES = {
  I:  { High: "30–40", Moderate: "21–29", Low: "10–20" },
  EO: { High: "30–40", Moderate: "21–29", Low: "10–20" },
  EC: { High: "30–40", Moderate: "21–29", Low: "10–20" },
};

const BAND_DESCRIPTIONS = {
  I: {
    High:     "You strongly exhibit an Internal Locus of Control — you believe your own actions, decisions, and effort are the primary determinants of outcomes. You are highly accountable and self-driven.",
    Moderate: "You moderately exhibit an Internal orientation. You take ownership of outcomes in most situations while acknowledging that external factors sometimes play a meaningful role.",
    Low:      "You only weakly exhibit an Internal orientation. You tend to attribute outcomes more to external factors than to your own actions, which can limit initiative and persistence.",
  },
  EO: {
    High:     "You strongly exhibit an External-Others orientation — you believe that powerful people and authority figures largely determine what happens to you. You are highly attuned to hierarchy and approval signals.",
    Moderate: "You moderately exhibit an External-Others orientation. You are politically aware and sensitive to the influence of key stakeholders while retaining reasonable confidence in your own agency.",
    Low:      "You only weakly exhibit an External-Others orientation. You rarely attribute outcomes to the decisions of others and are generally comfortable acting without senior endorsement.",
  },
  EC: {
    High:     "You strongly exhibit an External-Chance orientation — you believe that luck, fate, and unpredictable circumstances are major drivers of outcomes. This can reduce proactive planning and effort investment.",
    Moderate: "You moderately exhibit an External-Chance orientation. You acknowledge the role of luck and uncertainty without letting it dominate your decision-making or effort levels.",
    Low:      "You only weakly exhibit an External-Chance orientation. You are confident that consistent effort and planning — rather than luck — determine outcomes in most situations.",
  },
};

function getBandLabel(score) {
  if (score == null) return "—";
  if (score >= 30) return "High";
  if (score >= 21) return "Moderate";
  return "Low";
}

function getBandColor(label) {
  if (label === "High")     return "#17643b";
  if (label === "Moderate") return "#8a5a00";
  return "#9b1c1c";
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── TRIANGLE CHART SVG ───────────────────────────────────────────────────────
function TriangleSVG({ scores, dominantCode }) {
  const cx = 210, cy = 205;
  const R  = 120;
  const keys   = ["I", "EO", "EC"];
  const angles = [-90, 150, 30];
  const MAX    = 40;
  const size   = 420;

  function polar(deg, r) {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  const vertices = angles.map(a => polar(a, R));
  const rings    = [10, 20, 30, 40];

  const scorePoints = keys.map((k, i) => {
    const r = ((scores?.[k] ?? 0) / MAX) * R;
    return polar(angles[i], Math.max(r, 2));
  });
  const polyStr = scorePoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const labelConfig = [
    { key: "I",  anchor: "middle", dx: 0,   dy: -18 },
    { key: "EO", anchor: "end",    dx: -8,  dy:  16 },
    { key: "EC", anchor: "start",  dx:  8,  dy:  16 },
  ];

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}
      style={{ display: "block", margin: "0 auto", overflow: "visible", fontFamily: "Arial, sans-serif" }}>

      {/* Ring grid */}
      {rings.map(v => {
        const pts = angles.map(a => polar(a, (v / MAX) * R));
        return (
          <polygon key={v}
            points={pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")}
            fill="none" stroke="#DCE5EF" strokeWidth={1}
          />
        );
      })}

      {/* Ring value labels */}
      {rings.map(v => {
        const p = polar(-90, (v / MAX) * R);
        return (
          <text key={`rv-${v}`} x={p.x + 5} y={p.y} fontSize="9" fill={T.slate}
            dominantBaseline="middle">{v}</text>
        );
      })}

      {/* Axis spokes */}
      {vertices.map((v, i) => (
        <line key={i} x1={cx} y1={cy} x2={v.x} y2={v.y}
          stroke="#DCE5EF" strokeWidth={1.5} />
      ))}

      {/* Score polygon */}
      <polygon points={polyStr}
        fill={`${T.navy}22`} stroke={T.navy} strokeWidth={2.5} strokeLinejoin="round" />

      {/* Score dots */}
      {scorePoints.map((p, i) => {
        const code  = keys[i];
        const isDom = dominantCode === code;
        return (
          <circle key={i} cx={p.x} cy={p.y} r={7}
            fill={isDom ? SCALE_COLOR[code] : "#fff"}
            stroke={SCALE_COLOR[code]} strokeWidth={2.5}
          />
        );
      })}

      {/* Axis labels */}
      {labelConfig.map(({ key, anchor, dx, dy }, i) => {
        const v     = vertices[i];
        const color = SCALE_COLOR[key];
        const isDom = dominantCode === key;
        const def   = SCALE_DEFS.find(d => d.code === key);
        return (
          <g key={key}>
            <text x={v.x + dx} y={v.y + dy - 10} textAnchor={anchor}
              fontSize="13" fontWeight="700"
              fill={isDom ? color : T.ink}>
              {key}
            </text>
            <text x={v.x + dx} y={v.y + dy + 6} textAnchor={anchor}
              fontSize="10" fill={T.slate}>
              {def?.label}
            </text>
            {scores?.[key] != null && (
              <text x={v.x + dx} y={v.y + dy + 20} textAnchor={anchor}
                fontSize="11" fontWeight="700" fill={color}>
                {scores[key]}
              </text>
            )}
          </g>
        );
      })}

      {/* Centre label */}
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize="11"
        fill={T.slate} fontWeight="600">LOCUS</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="11"
        fill={T.slate} fontWeight="600">PROFILE</text>
    </svg>
  );
}

// ─── BAR CHART SVG ────────────────────────────────────────────────────────────
function BarChartSVG({ scores }) {
  const W   = 680;
  const H   = 260;  // tightened — 3 bars fit cleanly
  const PAD = { top: 36, right: 120, bottom: 52, left: 12 };
  const MAX = 40;
  const TRACK_W = W - PAD.left - PAD.right;   // 435px
  const keys   = ["I", "EO", "EC"];
  const labels = { I: "Internal", EO: "External-Others", EC: "External-Chance" };
  const barH   = 38;
  const gap    = 22;

  // barX = where the track starts (score = 10 baseline)
  const barX = PAD.left + (10 / MAX) * TRACK_W;
  // trackEnd = right edge of the track
  const trackEnd = PAD.left + TRACK_W;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%"
      style={{ display: "block", fontFamily: "Arial, sans-serif" }}>

      <defs>
        {keys.map(code => (
          <linearGradient key={code} id={`bar-grad-${code}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={SCALE_COLOR[code]} />
            <stop offset="100%" stopColor={`${SCALE_COLOR[code]}88`} />
          </linearGradient>
        ))}
      </defs>

      {/* Band zone backgrounds */}
      {[
        { from: 10, to: 20, fill: "rgba(155,28,28,0.05)",  label: "LOW"      },
        { from: 20, to: 29, fill: "rgba(138,90,0,0.05)",   label: "MODERATE" },
        { from: 29, to: 40, fill: "rgba(23,100,59,0.05)",  label: "HIGH"     },
      ].map(({ from, to, fill, label }) => {
        const x1 = PAD.left + (from / MAX) * TRACK_W;
        const x2 = PAD.left + (to   / MAX) * TRACK_W;
        return (
          <g key={label}>
            <rect x={x1} y={PAD.top} width={x2 - x1} height={H - PAD.top - PAD.bottom}
              fill={fill} />
            <text x={(x1 + x2) / 2} y={PAD.top - 8} textAnchor="middle"
              fontSize="9" fill={T.slate} fontWeight="600">
              {label}
            </text>
          </g>
        );
      })}

      {/* Vertical grid lines */}
      {[10, 20, 30, 40].map(v => {
        const x = PAD.left + (v / MAX) * TRACK_W;
        return (
          <g key={v}>
            <line x1={x} y1={PAD.top} x2={x} y2={H - PAD.bottom}
              stroke="#DCE5EF" strokeWidth={1} />
            <text x={x} y={H - PAD.bottom + 14} textAnchor="middle"
              fontSize="11" fill={T.slate}>{v}</text>
          </g>
        );
      })}

      {/* Bars */}
      {keys.map((code, i) => {
        const score     = scores?.[code] ?? 0;
        const y         = PAD.top + i * (barH + gap);
        const bw        = Math.max(0, ((score - 10) / (MAX - 10))) * (TRACK_W - (10 / MAX) * TRACK_W);
        const color     = SCALE_COLOR[code];
        const bandLabel = getBandLabel(score);
        const bandColor = getBandColor(bandLabel);

        // Fixed right-side positions — never depend on bar width
        const scoreX = trackEnd + 10;   // score number
        const pillX  = trackEnd + 38;   // pill rect start
        const pillW  = 68;

        return (
          <g key={code}>
            {/* Row label */}
            <text x={barX - 10} y={y + barH / 2} textAnchor="end"
              dominantBaseline="middle" fontSize="12" fontWeight="700" fill={color}>
              {labels[code]}
            </text>

            {/* Track background */}
            <rect x={barX} y={y}
              width={trackEnd - barX}
              height={barH} rx={6} fill="#EAF0F6" />

            {/* Filled bar */}
            {score > 10 && (
              <rect x={barX} y={y} width={bw} height={barH} rx={6}
                fill={`url(#bar-grad-${code})`} />
            )}

            {/* Score number — fixed position after track */}
            <text x={scoreX} y={y + barH / 2} dominantBaseline="middle"
              fontSize="13" fontWeight="700" fill={color}>{score}</text>

            {/* Band pill — fixed position */}
            <rect x={pillX} y={y + barH / 2 - 10} width={pillW} height={20} rx={10}
              fill={`${bandColor}15`} stroke={`${bandColor}40`} strokeWidth={1} />
            <text x={pillX + pillW / 2} y={y + barH / 2} textAnchor="middle"
              dominantBaseline="middle" fontSize="9" fontWeight="700" fill={bandColor}>
              {bandLabel.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* X-axis label */}
      <text x={PAD.left + TRACK_W / 2} y={H - 4}
        textAnchor="middle" fontSize="11" fill={T.slate} fontWeight="600">
        Score (10 – 40)
      </text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HiddenLOCOReport({ participantData, onComplete }) {
  const containerRef  = useRef(null);
  const hasGenerated  = useRef(false);

  // ── Load html2canvas + jsPDF once ─────────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id; s.src = src; s.onload = done; s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-loco-v1");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",          "jspdf-loco-v1");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  useEffect(() => {
    if (!participantData || hasGenerated.current) return;
    hasGenerated.current = true;

    const generate = async () => {
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      await new Promise((r) => setTimeout(r, 1400));

      const el = containerRef.current;
      if (!el) { onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210, A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".loco-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2, useCORS: true, allowTaint: true, logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth, height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData  = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW  = logicalW * MM_PER_PX;
          const naturalH  = logicalH * MM_PER_PX;
          const scaleToW  = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`LOCO_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("LOCO PDF generation failed", err);
      } finally {
        hasGenerated.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!participantData) return null;

  // ── Derive display values ──────────────────────────────────────────────────
  const {
    userInfo     = {},
    locoData     = {},
    scores       = {},   // { I, EO, EC }
    dominantCode = null,
    insightText  = "",
  } = participantData;

  const participant    = userInfo.name  || "—";
  const assessmentDate = formatDate(
    participantData.submission_time
        ? new Date(participantData.submission_time)
        : new Date()
);

  const scalesForDisplay = SCALE_DEFS.map(def => {
    const score      = scores[def.code] ?? null;
    const bandLabel  = getBandLabel(score);
    const isDominant = dominantCode === def.code;
    return { def, score, bandLabel, isDominant };
  });

  const dominantDef = SCALE_DEFS.find(d => d.code === dominantCode);
  const profileId    = locoData?.profile?.id               ?? null;
const profileMeta  = locoData?.profile                   ?? (profileId ? PROFILE_MAP[profileId] : null);
const profileLabel = profileMeta?.label                  ?? (profileId ? PROFILE_MAP[profileId]?.label            : "—");
const dominancePat = profileMeta?.dominance_pattern      ?? (profileId ? PROFILE_MAP[profileId]?.dominance_pattern : "—");
const profileGrade = profileMeta?.grade                  ?? {};
const profileText  = profileMeta?.report_text            ?? insightText ?? "";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed", top: "0", left: "-9999px",
        width: "1100px", opacity: 0, pointerEvents: "none", zIndex: -1,
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />

      {/* ── All CSS scoped under .loco-pdf-root ── */}
      <style>{`
/* ── Cover ── */
        .loco-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .loco-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .loco-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem;position:relative; z-index:2; }
        .loco-pdf-root .brand-left { display:flex; align-items:center; gap:1rem; }
        .loco-pdf-root .logo { width:60px; height:60px; border-radius:16px; display:grid; place-items:center; font-weight:900; font-size:1.1rem; letter-spacing:.06em; flex-shrink:0; }
        .loco-pdf-root .brand h1 { margin:0; font-size:1.2rem; letter-spacing:.07em; text-transform:uppercase; }
        .loco-pdf-root .brand p  {  margin:.15rem 0 0; color:rgba(255,255,255,.72); font-size:0.85rem; }
        .loco-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }
        .loco-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .loco-pdf-root .cover-title p  { text-align: justify;font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; }
        .loco-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .loco-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .loco-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .loco-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .loco-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .loco-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .loco-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:2rem; }

        .loco-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .loco-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .loco-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .loco-pdf-root {
          font-family: 'Inter', Arial, sans-serif;
          background: #F5F8FC;
          color: #1D2733;
          line-height: 1.55;
        }
        .loco-pdf-root .report { max-width:1100px; margin:0 auto; padding:1.5rem 1rem 3rem; }

        /* ── Page shell ── */
        .loco-pdf-root .loco-page {
          background: #ffffff;
          border: 1px solid #DCE5EF;
          border-radius: 24px;
          box-shadow: 0 14px 36px rgba(11,31,58,.10);
          overflow: hidden;
          margin-bottom: 2rem;
          page-break-after: always;
        }

        .loco-pdf-root .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2.5rem;
          border-bottom: 1px solid #DCE5EF;
        }
        .loco-pdf-root .page-inner {
          padding: 2rem 2.5rem 2.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.6rem;
        }

        .loco-pdf-root .page-inner h2{ margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Typography ── */
        .loco-pdf-root h1 { margin:0; font-size:2.4rem; font-weight:900; line-height:1.15; }
        .loco-pdf-root h2 { margin:0 0 .8rem; font-size:1.4rem; font-weight:800; color:#A80D2C; }
        .loco-pdf-root h3 { margin:0 0 .55rem; font-size:1.05rem; font-weight:700; }
        .loco-pdf-root h4 { margin:0 0 .55rem; font-size:.95rem; font-weight:700; }
        .loco-pdf-root p  { margin:0 0 .75rem; line-height:1.65; }
        .loco-pdf-root p:last-child { margin-bottom:0; }

        /* ── Cards & layout ── */
        .loco-pdf-root .card {
          background:#fff;
          border: 1px solid #DCE5EF;
          border-radius: 16px;
          padding: 1.3rem 1.5rem;
        }
        .loco-pdf-root .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.1rem;
        }
        .loco-pdf-root .three-col {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }
        .loco-pdf-root .lead {
          font-size: .9rem;
          line-height: 1.7;
          color: #1D2733;
        }

        /* ── Score tiles ── */
        .loco-pdf-root .score-tile {
          border-radius: 14px;
          border: 1px solid #DCE5EF;
          padding: 16px 18px;
          background: linear-gradient(180deg,#fff,#f8fbff);
        }
        .loco-pdf-root .score-tile .tile-label {
          font-size:.72rem; letter-spacing:.08em; text-transform:uppercase;
          color:#5B677A; font-weight:700;
        }
        .loco-pdf-root .score-tile .tile-score {
          font-size: 2.2rem; font-weight:700; margin-top:4px;
        }
        .loco-pdf-root .score-tile .tile-band {
          font-size:.72rem; font-weight:700; text-transform:uppercase;
          letter-spacing:.06em; margin-top:4px;
        }

        /* ── Section heading ── */
        .loco-pdf-root .section-label {
          font-size:.68rem; font-weight:800; letter-spacing:.16em;
          text-transform:uppercase; color:#A80D2C; margin-bottom:.3rem;
        }
        .loco-pdf-root .divider {
          border:none; border-top: 1px solid #DCE5EF; margin:.6rem 0;
        }

        /* ── Analysis items ── */
        .loco-pdf-root .analysis-item {
          border-radius: 14px;
          border: 1px solid #DCE5EF;
          padding: 1.1rem 1.3rem;
          margin-bottom: 0rem;
        }
        .loco-pdf-root .band-section {
          border-radius: 10px;
          padding: .55rem .85rem;
          margin-bottom: .4rem;
          border: 1px solid transparent;
        }

        /* ── Dev steps list ── */
        .loco-pdf-root .dev-list {
          margin: .5rem 0 0; padding-left: 1.3rem;
        }
        .loco-pdf-root .dev-list li {
          font-size: .88rem; line-height: 1.65; margin-bottom: .35rem;
          color: #1D2733;
        }

        /* ── Cover-specific ── */
        .loco-pdf-root .cover-meta {
          display: flex; gap: 1rem; flex-wrap: wrap; margin-top: 1.6rem;
        }
        .loco-pdf-root .meta-chip {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 10px;
          padding: .55rem 1.1rem;
          font-size: .82rem; color: rgba(255,255,255,.9);
        }
        .loco-pdf-root .meta-chip strong { display:block; font-size:.68rem; letter-spacing:.1em; text-transform:uppercase; color:rgba(255,255,255,.6); margin-bottom:2px; }

        /* ── Dominant banner ── */
        .loco-pdf-root .dominant-banner {
          border-radius: 16px;
          padding: 1.3rem 1.6rem;
          display: flex;
          align-items: flex-start;
          gap: 1.2rem;
        }
        .loco-pdf-root .dominant-icon {
          width:56px; height:56px; border-radius:14px;
          display:flex; align-items:center; justify-content:center;
          font-size:1.8rem; flex-shrink:0;
        }

        /* ── Back cover ── */
        .loco-pdf-root .back-cover { min-height:1556px; color:white;}
        .loco-pdf-root .back-cover .page-inner { min-height:1556px; padding:1.5rem; display:flex; flex-direction:column; justify-content:space-between; }
        .loco-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .loco-pdf-root .closing p  { text-align: justify; max-width:100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .loco-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .loco-pdf-root .contact { background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .loco-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .loco-pdf-root .contact strong { font-size:0.92rem; }
        .loco-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .loco-pdf-root .light { color:rgba(255,255,255,.66); }

        /* ── Info box ── */
        .loco-pdf-root .info-box {
          background: #eff6ff; border: 1px solid #bfdbfe;
          border-left: 4px solid #A80D2C;
          border-radius: 12px; padding: 1rem 1.3rem;
          font-size: .9rem; line-height:1.7; color:#1e3a5f;
        }
      `}</style>

      <div className="loco-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="loco-page cover">
            <div style={{ background: `linear-gradient(135deg, ${T.navy} 0%, #C4163A 50%, ${T.teal} 100%)`, minHeight: "1556px", flexDirection: "column", position: "relative" }}>
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>
              
              <div className="cover-inner">

                {/* Brand bar */}
                <div className="brand">
                  
                  <div className="badge">Confidential Individual Report</div>
                </div>

                {/* Title block */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, alignItems: "stretch", width: "100%", position: "relative", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Locus of Control Inventory</h2>
                    <p>
                      Based on Levenson's (1974) multidimensional framework. Measures your attribution
                      style across three independent dimensions — Internal, External-Others, and
                      External-Chance — which together reveal your tendency to attribute outcomes to
                      personal agency, powerful others, or chance.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta"><span>Participant</span><strong>{participant}</strong></div>
                    <div className="meta"><span>Assessment Date</span><strong>{assessmentDate}</strong></div>
                    <div className="meta"><span>Dominant Scale</span>
                      <strong>
                        {dominantDef ? `${dominantDef.code} — ${dominantDef.label}` : "—"}
                      </strong>
                    </div>
                  </div>

                  {userInfo.company && (
                    <div style={{ marginTop: "1rem", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 14, padding: ".9rem 1rem", display: "inline-flex", gap: "1.5rem", flexWrap: "wrap" }}>
                      {userInfo.company    && <div><span style={{ fontSize: ".72rem", color: "rgba(255,255,255,.65)" }}>Organisation</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.company}</strong></div>}
                      {userInfo.department && <div><span style={{ fontSize: ".72rem", color: "rgba(255,255,255,.65)" }}>Department</span><br /><strong style={{ fontSize: ".9rem" }}>{userInfo.department}</strong></div>}
                    </div>
                  )}

                  <div className="cover-badges">
                    {["Score Summary", "Triangle Profile", "Scale Analysis", "Development Guide", "Report Analysis"].map(b => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note">
                  <div>Based on Levenson's (1974) multidimensional locus of control framework.</div>
                  <div>Scored across three scales: I · EO · EC</div>
                </div>

              </div>
            </div>
          </section>


          {/* ══════════════════════════════════════════
              PAGE 2 — SCORE SUMMARY & TRIANGLE
          ══════════════════════════════════════════ */}
          <section className="loco-page">
            <div className="page-header">
              <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:T.slate }}>
                Score Summary
              </div>
              <div style={{ fontSize:".72rem", color:T.slate }}>
                {participant} · {assessmentDate}
              </div>
            </div>
            <div className="page-inner">

              {/* How to read */}
              <div className="info-box">
                <strong>How to read this report:</strong> Three independent scores are reported — Internal (I), External-Others (EO), and External-Chance (EC). Each ranges from <strong>10 to 40</strong>. Higher scores indicate stronger endorsement of that attribution style. Low = 10–20 · Moderate = 21–29 · High = 30–40.
              </div>

              {/* Score tiles */}
              <div>
                <h2>Scale Scores</h2>
                <div className="three-col">
                  {scalesForDisplay.map(({ def, score, bandLabel }) => {
                    const color  = SCALE_COLOR[def.code];
                    const bColor = getBandColor(bandLabel);
                    return (
                      <div key={def.code} className="score-tile">
                        <div className="tile-label">{def.label} ({def.code})</div>
                        <div className="tile-score" style={{ color }}>{score ?? "—"}</div>
                        <div className="tile-band"  style={{ color: bColor }}>{bandLabel}</div>
                        <div style={{ fontSize:".78rem", color:T.slate, marginTop:6, fontStyle:"italic" }}>
                          {def.tagline}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bar chart */}
              <div className="card">
                <h3 style={{ marginBottom:"1rem" }}>Score Comparison</h3>
                <BarChartSVG scores={scores} />
              </div>

              {/* Triangle + interpretation */}
              <div className="two-col" style={{ alignItems:"start" }}>
                <div className="card" style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <h3 style={{ alignSelf:"flex-start", marginBottom:"1rem" }}>Attribution Profile</h3>
                  <TriangleSVG scores={scores} dominantCode={dominantCode} />
                  <div style={{ display:"flex", gap:12, marginTop:12, flexWrap:"wrap", justifyContent:"center" }}>
                    {["I","EO","EC"].map(k => (
                      <div key={k} style={{ display:"flex", alignItems:"center", gap:5, fontSize:".78rem" }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:SCALE_COLOR[k] }} />
                        <span style={{ color:T.slate }}>{k}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3>Interpretation Framework</h3>
                  <p className="lead">
                    The LOCI is based on Rotter's (1966) concept of locus of control, later extended into a multidimensional model by Levenson (1974). The three-scale structure reflects the insight that "external" is not monolithic — believing powerful others control outcomes is meaningfully different from believing in chance.
                  </p>
                  <hr className="divider" />
                  <div style={{ display:"flex", flexDirection:"column", gap:".7rem" }}>
                    {scalesForDisplay.map(({ def, score, bandLabel }) => {
                      const color  = SCALE_COLOR[def.code];
                      const bColor = getBandColor(bandLabel);
                      return (
                        <div key={def.code} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                          <div style={{ fontSize:"1.3rem", flexShrink:0, marginTop:2 }}>{def.icon}</div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:".88rem", color }}>
                              {def.label}
                              <span style={{ marginLeft:8, fontSize:".72rem", background:`${bColor}15`, border:`1px solid ${bColor}40`, borderRadius:999, padding:"1px 8px", color:bColor }}>
                                {bandLabel}
                              </span>
                            </div>
                            <div style={{ fontSize:".82rem", color:T.slate, lineHeight:1.55 }}>{def.tagline}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {dominantDef && (
                    <>
                      <hr className="divider" />
                      <div style={{ background:`${SCALE_COLOR[dominantDef.code]}0d`, border:`1px solid ${SCALE_COLOR[dominantDef.code]}30`, borderRadius:12, padding:".8rem 1rem" }}>
                        <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".1em", fontWeight:700, color:SCALE_COLOR[dominantDef.code], marginBottom:4 }}>
                          Dominant Scale
                        </div>
                        <p style={{ margin:0, fontSize:".88rem", lineHeight:1.6, color:T.ink }}>
                          {BAND_DESCRIPTIONS[dominantDef.code]?.[getBandLabel(scores[dominantDef.code])]}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

            </div>
          </section>

          {/* ══════════════════════════════════════════
    PAGE 2B — PRIMARY PROFILE
══════════════════════════════════════════ */}
<section className="loco-page">
  <div className="page-header">
    <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:T.slate }}>
      Primary Profile
    </div>
    <div style={{ fontSize:".72rem", color:T.slate }}>
      {participant} · {assessmentDate}
    </div>
  </div>
  <div className="page-inner">

    {/* Profile heading */}
    <div>
      <div className="section-label">Profile Identification</div>
      <h2 style={{ marginBottom:".3rem" }}>Primary Profile</h2>
      <p style={{ color:T.slate, fontSize:".88rem", margin:"0 0 1rem" }}>
        Your responses map to a specific attribution profile. The profile below describes your dominant pattern and what it means in professional contexts.
      </p>
    </div>

    {/* Profile card */}
    <div className="card" style={{ borderLeft:`4px solid ${T.navy}`, padding:"1.4rem 1.6rem" }}>

      {/* Header row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12, marginBottom:"1rem" }}>
        <div>
          <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".1em", color:T.navy, fontWeight:700, marginBottom:4 }}>
            Profile: 
          </div>
          <h3 style={{ margin:"0 0 4px", fontSize:"1.15rem", color:T.navy }}>
            {profileLabel}
          </h3>
          <div style={{ fontSize:".82rem", color:T.slate }}>
            Dominance Pattern: <strong style={{ color:T.ink }}>{dominancePat}</strong>
          </div>
        </div>

        {/* Grade pills */}
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {["I","EO","EC"].map(code => {
            const grade  = profileGrade[code] ?? getBandLabel(scores[code]);
            const bColor = getBandColor(grade);
            return (
              <div key={code} style={{ textAlign:"center" }}>
                <div style={{ fontSize:".68rem", fontWeight:700, color:T.slate, marginBottom:3, textTransform:"uppercase", letterSpacing:".06em" }}>
                  {code}
                </div>
                <span style={{
                  display:"inline-block", padding:"3px 10px", borderRadius:999,
                  fontSize:".75rem", fontWeight:700,
                  background:`${bColor}18`, color:bColor, border:`1px solid ${bColor}40`,
                }}>
                  {grade}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <hr className="divider" />

      {/* Narrative */}
      <p style={{ margin:0, fontSize:".92rem", lineHeight:1.75, color:T.ink }}>
        {profileText || "—"}
      </p>
    </div>

    {/* Summary note — mirrors the sample report's summary box */}
    <div className="info-box" style={{ borderLeft:`4px solid ${T.navy}` }}>
      <strong>Summary:</strong>{" "}
      {dominantCode === "I"
        ? "This profile suggests a strong ownership mindset with realistic awareness that not everything is fully controllable. For mid-senior roles, that is usually a useful combination when paired with influence skills and stakeholder management."
        : dominantCode === "EO"
        ? "This profile reflects high sensitivity to the influence of others. Paired with strong relationship skills, it can be an asset. Development should focus on expanding the comfort zone for autonomous, self-initiated action."
        : "This profile reflects a pragmatic, balanced attribution style. The candidate likely uses contextual judgment across situations. Development should focus on building clearer personal decision rules to reduce ambiguity in high-stakes moments."
      }
    </div>

    {/* Interpretation brackets — mirrors sample HTML */}
    <div>
      <h3 style={{ color:T.navy, marginBottom:".75rem" }}>Interpretation Brackets</h3>
      <div className="three-col">
        {[
          { label:"Low",      range:"10–20", bg:"#ffe9e9", color:"#9b1c1c", note:"Weak endorsement of that attribution style." },
          { label:"Moderate", range:"21–29", bg:"#fff4d6", color:"#8a5a00", note:"Balanced or situational endorsement." },
          { label:"High",     range:"30–40", bg:"#e8f6ea", color:"#17643b", note:"Strong endorsement of that attribution style." },
        ].map(({ label, range, bg, color, note }) => (
          <div key={label} className="card" style={{ textAlign:"center", padding:"1rem" }}>
            <span style={{ display:"inline-block", padding:"4px 14px", borderRadius:999, fontSize:".8rem", fontWeight:700, background:bg, color, marginBottom:8 }}>
              {label} {range}
            </span>
            <p style={{ margin:0, fontSize:".82rem", color:T.slate, lineHeight:1.5 }}>{note}</p>
          </div>
        ))}
      </div>
    </div>

  </div>
</section>


          {/* ══════════════════════════════════════════
              PAGE 3 — UNDERSTANDING THE SCORES (I · EO)
          ══════════════════════════════════════════ */}
          <section className="loco-page">
            <div className="page-header">
              <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:T.slate }}>
                Understanding the Scores
              </div>
              <div style={{ fontSize:".72rem", color:T.slate }}>
                {participant} · {assessmentDate}
              </div>
            </div>
            <div className="page-inner">

              {/* Scale overviews */}
              <div>
                <div className="section-label">Scale Deep-Dives</div>
                <h2 style={{ marginBottom:".3rem" }}>Understanding the Scores</h2>
                <p style={{ color:T.slate, fontSize:".88rem", margin:"0 0 .5rem" }}>
                  A detailed description of each scale's meaning and its implications in professional contexts.
                </p>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
                {scalesForDisplay.slice(0, 2).map(({ def, score, bandLabel, isDominant }) => {
                  const color = SCALE_COLOR[def.code];
                  return (
                    <div key={def.code} className="card"
                      style={{
                        borderLeft: `4px solid ${color}`,
                        background: isDominant ? `linear-gradient(90deg,${color}06,#fff)` : "#fff",
                      }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:".6rem" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:"1.5rem" }}>{def.icon}</span>
                          <div>
                            <h4 style={{ color, margin:0 }}>{def.label} ({def.code})</h4>
                            <div style={{ fontSize:".78rem", color:T.slate, fontStyle:"italic" }}>{def.tagline}</div>
                          </div>
                        </div>
                        {score != null && (
                          <span style={{ fontSize:".82rem", fontWeight:700, background:`${color}18`, border:`1px solid ${color}40`, borderRadius:999, padding:"4px 14px", color, flexShrink:0 }}>
                            {score} · {bandLabel}
                          </span>
                        )}
                      </div>

                      <p style={{ fontSize:".88rem", lineHeight:1.65, color:T.ink, margin:"0 0 .6rem" }}>
                        {def.overview}
                      </p>
                      <p style={{ fontSize:".85rem", color:T.slate, lineHeight:1.6, margin:"0 0 .75rem" }}>
                        {def.orgNote}
                      </p>

                      {/* Active band */}
                      {(() => {
                        const range = BAND_RANGES[def.code]?.[bandLabel] ?? "";
                        const desc  = BAND_DESCRIPTIONS[def.code]?.[bandLabel] ?? "";
                        return (
                          <div className="band-section active-band" style={{ border:`1px solid ${color}40`, background:`${color}08`, marginBottom:".75rem" }}>
                            <div style={{ fontWeight:700, fontSize:".8rem", color, marginBottom:".25rem" }}>
                              {bandLabel} ({range})
                              <span style={{ marginLeft:8, fontSize:".68rem", background:color, color:"#fff", borderRadius:999, padding:"1px 7px" }}>
                                Your band
                              </span>
                            </div>
                            <p style={{ margin:0, fontSize:".83rem", lineHeight:1.6, color:T.ink }}>{desc}</p>
                          </div>
                        );
                      })()}

                      <div>
                        <strong style={{ fontSize:".85rem", color:T.navy }}>Development steps:</strong>
                        <ul className="dev-list">
                          {def.devSteps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </section>


          {/* ══════════════════════════════════════════
              PAGE 4 — UNDERSTANDING THE SCORES (EC)
          ══════════════════════════════════════════ */}
          <section className="loco-page">
            <div className="page-header">
              <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:T.slate }}>
                Understanding the Scores
              </div>
              <div style={{ fontSize:".72rem", color:T.slate }}>
                {participant} · {assessmentDate}
              </div>
            </div>
            <div className="page-inner">

              {/* Scale overviews */}
              <div>
                <div className="section-label">Scale Deep-Dives</div>
                <h2 style={{ marginBottom:".3rem" }}>Understanding the Scores</h2>
                <p style={{ color:T.slate, fontSize:".88rem", margin:"0 0 .5rem" }}>
                  A detailed description of each scale's meaning and its implications in professional contexts.
                </p>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
                {scalesForDisplay.slice(2).map(({ def, score, bandLabel, isDominant }) => {
                  const color = SCALE_COLOR[def.code];
                  return (
                    <div key={def.code} className="card"
                      style={{
                        borderLeft: `4px solid ${color}`,
                        background: isDominant ? `linear-gradient(90deg,${color}06,#fff)` : "#fff",
                      }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:".6rem" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:"1.5rem" }}>{def.icon}</span>
                          <div>
                            <h4 style={{ color, margin:0 }}>{def.label} ({def.code})</h4>
                            <div style={{ fontSize:".78rem", color:T.slate, fontStyle:"italic" }}>{def.tagline}</div>
                          </div>
                        </div>
                        {score != null && (
                          <span style={{ fontSize:".82rem", fontWeight:700, background:`${color}18`, border:`1px solid ${color}40`, borderRadius:999, padding:"4px 14px", color, flexShrink:0 }}>
                            {score} · {bandLabel}
                          </span>
                        )}
                      </div>

                      <p style={{ fontSize:".88rem", lineHeight:1.65, color:T.ink, margin:"0 0 .6rem" }}>
                        {def.overview}
                      </p>
                      <p style={{ fontSize:".85rem", color:T.slate, lineHeight:1.6, margin:"0 0 .75rem" }}>
                        {def.orgNote}
                      </p>

                      {/* Active band */}
                      {(() => {
                        const range = BAND_RANGES[def.code]?.[bandLabel] ?? "";
                        const desc  = BAND_DESCRIPTIONS[def.code]?.[bandLabel] ?? "";
                        return (
                          <div className="band-section active-band" style={{ border:`1px solid ${color}40`, background:`${color}08`, marginBottom:".75rem" }}>
                            <div style={{ fontWeight:700, fontSize:".8rem", color, marginBottom:".25rem" }}>
                              {bandLabel} ({range})
                              <span style={{ marginLeft:8, fontSize:".68rem", background:color, color:"#fff", borderRadius:999, padding:"1px 7px" }}>
                                Your band
                              </span>
                            </div>
                            <p style={{ margin:0, fontSize:".83rem", lineHeight:1.6, color:T.ink }}>{desc}</p>
                          </div>
                        );
                      })()}

                      <div>
                        <strong style={{ fontSize:".85rem", color:T.navy }}>Development steps:</strong>
                        <ul className="dev-list">
                          {def.devSteps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="two-col" style={{ marginTop:"0rem" }}>
                <div className="card">
                  <h3>Understanding the Three Scales</h3>
                  <p className="lead">
                    Internal, External-Others, and External-Chance are independent dimensions — not a single spectrum. A person can score high on both I and EO, reflecting a belief that personal effort matters and that powerful relationships also shape outcomes. Profiles should be read holistically, not as a ranking.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    Locus of control is shaped by experience, culture, and context. With targeted development, people can shift their attribution style — strengthening internal agency where it is low, or moderating excessive internal attributions by building greater systemic and relational awareness.
                  </p>
                </div>
              </div>

            </div>
          </section>
{/*  */}
{/*  */}
{/*  */}
{/*  */}
{/*  */}
          <section className="loco-page">
            <div className="page-header">
              <div style={{ fontSize:".72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:T.slate }}>
                Development Guidance
              </div>
              <div style={{ fontSize:".72rem", color:T.slate }}>
                {participant} · {assessmentDate}
              </div>
            </div>

            <div className="page-inner">
              {/* Development guidance table */}
              <div>
                <div className="section-label" style={{ marginTop:".5rem" }}>Focused Guidance</div>
                <h2 style={{ marginBottom:".8rem" }}>Development Guidance</h2>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr>
                        {["Focus Area", "Recommendation"].map(h => (
                          <th key={h} style={{
                            background:"#eff6ff", color:T.navy,
                            border:`1px solid ${T.line}`, padding:"10px 14px",
                            textAlign:"left", fontSize:".82rem", fontWeight:700,
                            textTransform:"uppercase", letterSpacing:".06em",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          area: "Development Priority",
                          rec: dominantCode === "I"
                            ? "Strengthen strategic influence and stakeholder mapping so effort converts to visible outcomes more consistently."
                            : dominantCode === "EO"
                            ? "Build independent decision-making capability. Practise committing to a course of action without waiting for senior validation."
                            : "Strengthen clear decision rules: when to act independently, when to align, and when to wait.",
                        },
                        {
                          area: "Coaching Focus",
                          rec: dominantCode === "I"
                            ? "Differentiate between controllable effort and uncontrollable factors. Build a habit of planning for uncertainty without reducing ownership."
                            : dominantCode === "EO"
                            ? "Identify the internal compass — what do you believe is right when no one is watching? Use that as a baseline for autonomous action."
                            : "Work on identifying default triggers for each attribution style. Increase self-awareness around when each lens is most useful.",
                        },
                        {
                          area: "Manager Support",
                          rec: dominantCode === "I"
                            ? "Give clear goals, timely feedback, and decision boundaries. Recognise outcomes publicly so the candidate sees the link between action and result."
                            : dominantCode === "EO"
                            ? "Create protected zones for autonomous decision-making. Avoid over-supervising — it reinforces the belief that outcomes depend on the manager."
                            : "Provide structured frameworks for decision-making in ambiguous situations. Clarity of expectations reduces paralysis.",
                        },
                        {
                          area: "Behavioural Watch",
                          rec: dominantCode === "I"
                            ? "Monitor for frustration or burnout when external factors block effort-driven results. Build resilience for situations where effort alone is insufficient."
                            : dominantCode === "EO"
                            ? "Watch for deference patterns in meetings — are they consistently deferring to authority even on topics within their own expertise?"
                            : "Observe whether uncertainty is used as a reason to delay action. Support the candidate in separating analysis from paralysis.",
                        },
                      ].map(({ area, rec }, i) => (
                        <tr key={area} style={{ background: i % 2 === 0 ? "#fff" : "#fafcff" }}>
                          <td style={{
                            border:`1px solid ${T.line}`, padding:"10px 14px",
                            fontSize:".88rem", fontWeight:700, color:T.navy,
                            whiteSpace:"nowrap", verticalAlign:"top",
                          }}>{area}</td>
                          <td style={{ border:`1px solid ${T.line}`, padding:"10px 14px", fontSize:".88rem", lineHeight:1.65 }}>
                            {rec}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Behavioural interpretation summary */}
              <div className="two-col">
                {[
                  {
                    title: "Work Style",
                    icon: "💼",
                    body: dominantCode === "I"
                      ? "Self-driven, accountable, and outcome-oriented. Likely to take initiative, follow through on commitments, and push for execution even when conditions are not ideal."
                      : dominantCode === "EO"
                      ? "Attentive to stakeholder signals and hierarchical expectations. Works well in structured environments with clear chains of authority."
                      : "Pragmatic and adaptive. Tends to pace effort against perceived predictability of the environment.",
                  },
                  {
                    title: "Leadership Implication",
                    icon: "🚀",
                    body: dominantCode === "I"
                      ? "Useful in roles requiring ownership, delivery discipline, and confidence in decision-making. Can model responsibility for team performance."
                      : dominantCode === "EO"
                      ? "Effective in relationship-driven leadership where buy-in from power holders is critical. May need support to act decisively without top-down endorsement."
                      : "Brings flexible judgment in uncertain environments. Leadership strength may lie in contextual reasoning rather than a fixed operating style.",
                  },
                ].map(({ title, icon, body }) => (
                  <div key={title} className="card">
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:".6rem" }}>
                      <span style={{ fontSize:"1.2rem" }}>{icon}</span>
                      <h3 style={{ margin:0, color:T.navy }}>{title}</h3>
                    </div>
                    <p style={{ margin:0, fontSize:".88rem", lineHeight:1.65 }}>{body}</p>
                  </div>
                ))}
              </div>

              {/* AI Insight text if provided */}
              {insightText && (
                <div className="info-box">
                  <strong>Insight:</strong> {insightText}
                </div>
              )}
            </div>
          </section>


          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="loco-page back-cover">
            <div style={{ background:`linear-gradient(135deg, ${T.navy} 0%, #C4163A 50%, ${T.teal} 100%)`,minHeight:"1556px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"1.5rem 2.5rem", borderBottom:"1px solid rgba(255,255,255,0.15)" }}>
                <div style={{ fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:"rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.5)", letterSpacing:".05em" }}>Acumen Business Catalyst</div>
              </div>
              <div className="page-inner"  >
                <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1,padding:"0 2.5rem 0 2.0rem" }}>
                  <div className="closing">
                    <h2>Developing Your Locus of Control</h2>
                    <p>
                      Attribution style is a dynamic capability, not a fixed trait. Understanding whether you tend to attribute outcomes to your own effort, the influence of powerful others, or the role of chance — and the conditions under which each attribution serves or limits you — is the foundation of deliberate development. Acumen Business Catalyst uses assessments like this to turn behavioural data into focused conversations that build lasting professional effectiveness.
                    </p>
                  </div>
                </div>
                <div>
                  <div className="contact-grid">
                    <div className="contact">
                      <span>Brand</span>
                      <strong>Acumen Business Catalyst</strong>
                    </div>
                    <div className="contact">
                      <span>Focus</span>
                      <strong>Leadership · Culture · Capability</strong>
                    </div>
                    <div className="contact">
                      <span>Report type</span>
                      <strong>Individual LOCI Profile</strong>
                    </div>
                  </div>
                  <p className="light" style={{ marginTop:"1.5rem", fontSize:"0.75rem" }}>
                    This report is generated from the Locus of Control Inventory (LOCI). Based on Levenson's (1974) multidimensional locus of control framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}